import { EllipsisHorizontalIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import cx from 'classnames'
import { type FC, type MouseEventHandler } from 'react'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

export interface MenuButtonProps extends DefaultProps {
  /**
   * Click handler
   */
  onClick: MouseEventHandler<HTMLButtonElement>
  /**
   * Display vertical ellipsis instead of horizontal
   */
  vertical?: boolean
  /**
   * Use inverse color for the icon, when inverseColor is off, the icon will be white with blur effect
   */
  inverseColor?: boolean

  /**
   * Disabled
   */
  disabled?: boolean
}

export const MenuButton: FC<MenuButtonProps> = ({
  onClick,
  vertical = false,
  inverseColor = false,
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
  disabled = false,
}) => {
  // Component base class names. Can be overriden by className property.
  const baseClass = cx('h-10 w-10 rounded-[4px]', {
    'hover:bg-gray-50': inverseColor,
    'backdrop-blur-sm bg-black/[0.10] filter drop-shadow-2xl': !inverseColor,
    'cursor-not-allowed': disabled,
    'h-[unset] w-[unset]': vertical,
  })

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  // Icon class names with conditional color
  const iconClasses = cx('z-1 h-10 w-10', {
    'text-text-header': inverseColor,
    'text-text-reverse': !inverseColor,
    'text-text-disabled': disabled,
    'h-5 w-5': vertical,
  })

  const IconComponent = vertical ? EllipsisVerticalIcon : EllipsisHorizontalIcon

  return (
    <button
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      onClick={onClick}
      type="button"
      disabled={disabled}
    >
      <IconComponent className={iconClasses} />
    </button>
  )
}
